import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';

/** Chromium fires this before showing its own install UI; Safari has no equivalent. */
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
    }
    const nav = window.navigator as Navigator & { standalone?: boolean };
    return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
}

function isIos(): boolean {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function InstallCard(): ReactElement | null {
    const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
    const [installed, setInstalled] = useState(isStandalone);

    useEffect(() => {
        function onPrompt(event: Event): void {
            event.preventDefault();
            setDeferred(event as BeforeInstallPromptEvent);
        }
        function onInstalled(): void {
            setInstalled(true);
            setDeferred(null);
        }
        window.addEventListener('beforeinstallprompt', onPrompt);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onPrompt);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    if (installed) {
        return null;
    }

    async function install(): Promise<void> {
        if (!deferred) {
            return;
        }
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === 'accepted') {
            setInstalled(true);
        }
        setDeferred(null);
    }

    return (
        <Card title="Add to Home Screen" headingLevel={2}>
            <p>
                Installed, the app opens full screen with its own icon and is one tap away during a
                night feed.
            </p>
            {deferred ? (
                <div>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            void install();
                        }}
                    >
                        Install app
                    </Button>
                </div>
            ) : isIos() ? (
                <p>
                    In Safari, tap the Share button, then <strong>Add to Home Screen</strong>.
                </p>
            ) : (
                <p>Open the browser menu and choose Install app or Add to Home Screen.</p>
            )}
        </Card>
    );
}
